import React from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useFormikContext } from "formik";
import Input from "../../../../components/form/Input";
import {
  namesForCreate,
  previewSummary,
  slotSuffixes,
} from "../../../../utils/locationNameRange";
import { text, previewCard } from "./modalStyles";

const creatingOptions = [
  ["single", "Single location"],
  ["multiple", "Multiple locations"],
];

// One line per name, reading "RC05A01 Left, Center, and Right" -- no columns.
// The list is not itself scrollable: DialogContent already scrolls, which keeps
// one scrollbar on screen and the action buttons pinned outside it.
const styles = {
  entryList: {
    mt: 1.25,
    display: "flex",
    flexDirection: "column",
    rowGap: 0.25,
  },
  slotName: { color: "text.secondary", ml: 0.75 },
  // The examples sit on their own line, under the sentence that introduces them.
  helpExamples: { display: "block" },
};

// Wired straight to Formik rather than through components/form/RadioGroup:
// that wrapper renders labels at 14px text.secondary (fainter and smaller than
// the label above them), and its `value={field.value || undefined}` flips the
// group from uncontrolled to controlled on first pick, which MUI warns about.
export function CreatingModeField() {
  const { values, setFieldValue } = useFormikContext();

  return (
    // 28px replaces the "Creating" label that used to sit here (24px line box
    // plus its 4px margin), so the gap under the title is unchanged. It cannot
    // come from DialogContent's padding-top: MUI zeroes that with
    // `.MuiDialogTitle-root + .MuiDialogContent-root`, which outranks sx.
    <Box sx={{ mt: 3.5 }}>
      <RadioGroup
        row
        name="creatingMode"
        value={values.creatingMode || ""}
        onChange={event => setFieldValue("creatingMode", event.target.value)}
      >
        {creatingOptions.map(([value, label]) => (
          <FormControlLabel
            key={value}
            value={value}
            sx={{ mr: 3 }}
            control={<Radio size="small" color="primary" />}
            label={<Typography sx={text.choiceLabel}>{label}</Typography>}
          />
        ))}
      </RadioGroup>
    </Box>
  );
}

export function LocationNameFields() {
  const { values } = useFormikContext();

  if (values.creatingMode !== "multiple") {
    return <Input size="medium" name="locationName" label="Location Name" showNAButton={false} />;
  }

  return (
    <Box>
      <Box display="flex" gap={1.5}>
        <Input size="medium" name="locationFrom" label="From" showNAButton={false} />
        <Input size="medium" name="locationTo" label="To" showNAButton={false} />
      </Box>
      {/* 14px is MuiOutlinedInput's own input padding-left, so this lines up
          with the text inside the From field rather than its border box --
          the same offset MUI gives outlined helper text. */}
      <Typography sx={{ ...text.help, mt: 0.75, pl: "14px" }}>
        The last number increments such as:
        <Box component="span" sx={styles.helpExamples}>
          V001 to V020 or RA0401 to RA0410
        </Box>
      </Typography>
    </Box>
  );
}

const joinSlotNames = slotNames => {
  const named = slotNames.filter(Boolean);

  if (named.length === 0) return "";
  if (named.length === 1) return named[0];
  if (named.length === 2) return `${named[0]} and ${named[1]}`;

  return `${named.slice(0, -1).join(", ")}, and ${named[named.length - 1]}`;
};

export function LocationPreview() {
  const { values } = useFormikContext();
  const { names, error } = namesForCreate(values);
  const suffixes = slotSuffixes(values.locationType, values.slot, values.customSlot);
  const summary = previewSummary(names, values.locationType, values.slot, values.customSlot);
  // " (Left)" -> "Left"; a non-rack location has a single empty suffix, which
  // filters out to an empty list and leaves the name standing alone.
  const slotNames = suffixes.map(suffix => suffix.replace(/[()\s]/g, ""));
  const slotList = joinSlotNames(slotNames);

  if (error) {
    return <Typography sx={{ ...text.help, color: "error.main", mt: 1.5 }}>{error}</Typography>;
  }

  if (!summary) return null;

  return (
    <Box sx={previewCard}>
      <Typography sx={text.summary}>{summary}</Typography>
      <Box sx={styles.entryList}>
        {names.map(name => (
          <Typography key={name} sx={text.previewName}>
            {name}
            {slotList && (
              <Box component="span" sx={styles.slotName}>
                {slotList}
              </Box>
            )}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}
